import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { isNotEmptyString, isNotNullOrUndefined, withDefault } from '@vy/utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { usePlansOverviewsWithPageParams } from '../../../../../hooks/requests/use-plans-overview/use-plans-overviews';
import { useDialog } from '../../../../../hooks/use-dialog';
import { usePaginationResetHandler } from '../../../../../hooks/use-pagination-reset-handler';
import type { NewPlanAssignment } from '../../plans-page-types/new-plan-assignment-schema';
import { NewPlanAssignmentSchema } from '../../plans-page-types/new-plan-assignment-schema';
import type { PlanAssignmentInput } from '../../plans-page-types/plan-assignment-input-schema';
import { CreateContractSidebar } from '../CreateContractSidebar';
import { PlansOverviewTable } from '../PlansOverviewTable';
import { PlansOverviewsTableToolbar } from './plans-tab-content-components/PlansOverviewsTableToolbar';
import { useTablePagination } from '../../../../../hooks/use-table-pagination';

const PlansTabContent: React.FC = () => {
  const { page, pageSize, onPageChange, onPageSizeChange } = useTablePagination();

  const { data: plansOverviewsResponse, isFetching: isFetchingPlansOverviews } = useTablePlansOverviews({
    page,
    pageSize,
  });

  usePaginationResetHandler(plansOverviewsResponse?.plansOverviews.length ?? 0);

  const {
    isDialogOpen: isFilterPlanOpen,
    handleOpen: openFilterPlan,
    handleClose: closeFilterPlan,
  } = useDialog();

  const methods = useForm<PlanAssignmentInput, NewPlanAssignment>({
    resolver: zodResolver(NewPlanAssignmentSchema),
  });

  const contract = methods.watch('contract');

  return (
    <div className="flex h-full flex-col">
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          alignContent: 'center',
          width: '100%',
          paddingY: 1.5
        }}>
        <Typography
          sx={{
            fontSize: 24,
            width: '23%'
          }}>
          Plans and Pricing
        </Typography>
      </Stack>
      <FormProvider {...methods}>
        <Paper
          elevation={1}
          sx={{
            height: '100%',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          component="form"
        >
          <PlansOverviewsTableToolbar
            isFilterPlanOpen={isFilterPlanOpen}
            openFilterPlan={openFilterPlan}
            closeFilterPlan={closeFilterPlan}
          />

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <PlansOverviewTable
              items={withDefault(plansOverviewsResponse?.plansOverviews, [])}
              itemsCount={withDefault(plansOverviewsResponse?.countedFilteredPlansOverviews, 0)}
              loadingItems={isFetchingPlansOverviews}
              isPlanFilterOpen={isFilterPlanOpen}
              page={page}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </Box>

          {isNotNullOrUndefined(contract) && isNotEmptyString(contract.planId) && (
            <CreateContractSidebar title="Assign customer" planId={contract.planId} />
          )}
        </Paper>
      </FormProvider>
    </div>
  );
};

const PlansTabContentWithSuspenseBoundary = withSuspenseBoundary(PlansTabContent);
export { PlansTabContentWithSuspenseBoundary as PlansTabContent };

function useTablePlansOverviews(pagination: { page: number; pageSize: number }) {
  const { data: plansOverviewsResponse, ...restPlansOverviewsResponse } =
    usePlansOverviewsWithPageParams(pagination);

  return {
    ...restPlansOverviewsResponse,
    data: plansOverviewsResponse,
  };
}
